import { useEffect } from "react";

import def from "#/def/lookup-tables.schema.gen";
import { filtertByEnvironment, validateIdent } from "#/utils/LookupTableUtils";

import { useDataLayerQuery } from "./useDataLayer";

/**
 * Generates lookup table specific params for data layer hooks
 *
 * @param {string} ident
 * @param {string} mode
 * @param {object} dataLayer
 * @returns
 */
export function lookupTablesDataLayerParams(
  ident,
  mode = "offline",
  dataLayer = null
) {
  validateIdent(ident, def);

  return {
    ident: def[ident].path,
    mode,
    service: {
      usePageEndpoint: false,
      baseUrl: `/api/${def[ident].path}`,
    },
    dataLayer,
  };
}

/**
 * Returns list of items of the lookup-table, filtered by environment.
 *
 * We return an empty list until the query is resolved,
 * to avoid returning null which would likely cause errors in the components that consume this hook.
 *
 * This hook is intentionaly simplified to only return the list of items,
 * and not the loading or error state,
 *
 * It will automatically re-query when the ident changes, or when the online status changes,
 *
 * We always use full cache for lookup tables, since they are usually small and
 * we want to minimize the number of requests to the service.
 *
 * @param {string} ident
 * @param {object} dataLayer Optional custom dataLayer to use for the query
 * @returns
 */
export function useLookupTableList(
  ident,
  dataLayer = null,
  { skipEnvironmentFilter = false } = {}
) {
  // use cached mode for these common lookup-table hooks
  const query = useDataLayerQuery(
    lookupTablesDataLayerParams(ident, "offline", dataLayer)
  );

  useEffect(() => {
    query.reload({
      // get everything
      pageSize: 0,
    });
  }, []);

  return skipEnvironmentFilter
    ? query.result.content
    : query.result.content.filter(filtertByEnvironment);
}

/**
 * Returns a single item of the lookup-table by id, filtered by environment.
 *
 * We fail gracefully and return null if id is not provided,
 * since in that case we usually want to return an "empty" item, a
 * nd not perform a lookup with id=null which would likely
 * return an unintended item.
 *
 * @param {string} ident
 * @param {string} id
 * @param {object} dataLayer Optional custom dataLayer to use for the query
 * @returns
 */
export function useLookupTableResolver(ident, id, dataLayer = null) {
  validateIdent(ident, def);

  return (
    useLookupTableList(ident, dataLayer).find((item) => item["sifra"] === id) ??
    null
  );
}
